/**
 * Phase 6 — Client-actor surfaces.
 * Used inside JobDetail (billable summary, completion confirm/contest, rate lead).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Stat } from "./_shared";


export function ClientBillableSummary({ jobId, bookingId, bookingState }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api(`/jobs/${jobId}/bookings/${bookingId}/billable-summary`);
        setData(r);
      } catch {
        setData(null);
      } finally { setLoading(false); }
    })();
  }, [jobId, bookingId]);

  if (loading || !data) return null;
  if (!["completed", "awaiting_client_confirmation"].includes(bookingState)) return null;

  return (
    <div className="avcp-card p-5" data-testid={TID.clientBillableSummary}>
      <h3 className="font-semibold mb-2">Billable summary</h3>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Aggregate Lead Technician hours for this booking. Helper labor remains
        between the Lead and the helpers per Workforce Partners marketplace rules.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 text-sm">
        <Stat label="Lead hours" value={data.total_billable_hours != null
                                          ? `${data.total_billable_hours}h` : "—"} />
        <Stat label="Lead rate" value={data.lead_rate_snapshot != null
                                         ? `${data.currency} ${data.lead_rate_snapshot}/h` : "—"} />
        <Stat label="Indicative total" value={data.indicative_total != null
                                                ? `${data.currency} ${data.indicative_total}` : "—"} />
      </div>
    </div>
  );
}


export function ClientCompletionPanel({ jobId, bookingId, bookingState, onChange }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (bookingState !== "awaiting_client_confirmation") return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await api(`/jobs/${jobId}/bookings/${bookingId}/confirm-completion`,
                  { method: "POST", body: {} });
      toast.success("Project marked complete. You may now rate your Lead Technician.");
      onChange && onChange();
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { setBusy(false); }
  };

  const contest = async () => {
    if (!reason.trim()) { toast.error("Please describe what is incomplete."); return; }
    setBusy(true);
    try {
      await api(`/jobs/${jobId}/bookings/${bookingId}/contest-completion`,
                  { method: "POST", body: { reason } });
      toast.success("Contest raised. Workforce Partners support will review.");
      onChange && onChange();
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="avcp-card p-5 border-l-4 border-l-amber-500">
      <h3 className="font-semibold mb-2">Confirm completion</h3>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        The Lead Technician has marked this project complete. Please confirm so the
        booking can close, or contest if work is unfinished. Auto-confirmation
        happens 7 days after the Lead&apos;s submission unless you act.
      </p>
      <div className="flex gap-2 mb-3">
        <button className="avcp-btn-primary" disabled={busy}
                data-testid={TID.clientConfirmCompletion}
                onClick={confirm}>Confirm completion</button>
      </div>
      <div className="text-sm">
        <p className="text-xs text-[var(--avcp-ink-soft)] mb-1">Or contest:</p>
        <div className="flex gap-2">
          <input className="avcp-input flex-1"
                  placeholder="Briefly describe what is incomplete"
                  data-testid={TID.clientContestReason}
                  value={reason} onChange={(e) => setReason(e.target.value)} />
          <button className="avcp-btn-secondary" disabled={busy}
                  data-testid={TID.clientContestCompletion}
                  onClick={contest}>Contest</button>
        </div>
      </div>
    </div>
  );
}


export function ClientRateLeadForm({ jobId, bookingId, bookingState }) {
  const [score, setScore] = useState(5);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (bookingState !== "completed") return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api(`/jobs/${jobId}/bookings/${bookingId}/rating`, {
        method: "POST",
        body: {
          score: parseInt(score, 10),
          comment: comment || undefined,
        },
      });
      toast.success("Thanks — your rating was submitted.");
      setSubmitted(true);
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="avcp-card p-5 bg-[var(--avcp-surface-2)]">
        <p className="text-sm">✓ Rating submitted.</p>
      </div>
    );
  }

  return (
    <div className="avcp-card p-5" data-testid={TID.clientRateLeadForm}>
      <h3 className="font-semibold mb-2">Rate your Lead Technician</h3>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Your rating is anonymous to the Lead Technician. It does not influence
        invoicing or future eligibility.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <select className="avcp-input" value={score}
                data-testid={TID.clientRateLeadScore}
                onChange={(e) => setScore(e.target.value)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>
          ))}
        </select>
        <input className="avcp-input flex-1 min-w-[180px]"
                placeholder="Optional comment"
                data-testid={TID.clientRateLeadComment}
                value={comment} onChange={(e) => setComment(e.target.value)} />
        <button className="avcp-btn-primary" disabled={submitting}
                data-testid={TID.clientRateLeadSubmit}
                onClick={submit}>{submitting ? "Sending…" : "Submit rating"}</button>
      </div>
    </div>
  );
}
